// Renders the block data in src/content/pages/* as marketing pages.
import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import NotFoundPage from 'pages/NotFoundPage';
import PAGES from 'content/pages';
import { COLORS, FONTS, HEADING, TYPE } from 'styles/theme';
import {
    MQ, PAD, WIDTH, SPACE, RADIUS, SURFACE, MEDIA, SHAPE, WRAP,
    BUTTON, BUTTON_OUTLINE, BUTTON_DARK, MARK,
    container, toneOf, variantOf, collageOf, galleryRowWidth,
} from 'styles/blocks';
import { useMediaQuery } from 'hooks/useMediaQuery';
import ContactFormBlock from 'components/marketing/ContactFormBlock';

const useIsWide = () => useMediaQuery(MQ.band);

// mailto:/tel: stay in the current tab — a new window would just sit blank.
function ActionLink({ action, style }) {
    if (action.to) return <Link to={action.to} style={style}>{action.label}</Link>;
    const sameTab = /^(mailto:|tel:)/.test(action.href);
    return (
        <a
            href={action.href}
            style={style}
            {...(sameTab ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        >
            {action.label}
        </a>
    );
}

function Cta({ block }) {
    return (
        <div style={{ ...WRAP, padding: `8px ${PAD}px 24px` }}>
            <ActionLink action={block} style={BUTTON} />
        </div>
    );
}

// Centered heading over a row of buttons, on its own tinted full-bleed strip.
function CtaGroup({ block }) {
    return (
        <section style={{
            background: toneOf(block.tone, 'warm'), textAlign: 'center',
            padding: `64px ${PAD}px`, margin: '48px 0 0',
        }}>
            {block.heading && (
                <h2 style={{ ...HEADING, fontSize: TYPE.sectionHeading, margin: '0 0 22px', color: COLORS.dark }}>
                    {block.heading}
                </h2>
            )}
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {block.actions.map((action) => (
                    <ActionLink
                        key={action.label}
                        action={action}
                        style={action.variant === 'outline' ? BUTTON_OUTLINE : BUTTON}
                    />
                ))}
            </div>
        </section>
    );
}

// Two overlapping frames in a split's image column. First item is the back
// frame, second the larger low-left one. `variant: 'rect'` swaps the default
// circular crop for rounded-corner photos — see COLLAGE.
function SplitCollage({ items, order, variant }) {
    const [back, front] = items;
    const shape = collageOf(variant);
    const frame = (item, at, z) => (
        <img
            src={item.image}
            alt={item.imageAlt || ''}
            loading="lazy"
            style={{
                position: 'absolute', ...at, aspectRatio: shape.aspect, objectFit: 'cover',
                borderRadius: typeof shape.radius === 'number' ? `${shape.radius}px` : shape.radius,
                display: 'block', zIndex: z,
                ...(shape.shadow ? { boxShadow: shape.shadow } : null),
            }}
        />
    );
    return (
        <div style={{
            order, position: 'relative', width: '100%', aspectRatio: '1 / 1',
            maxWidth: `${shape.maxWidth}px`, justifySelf: 'center', alignSelf: 'center',
        }}>
            {frame(back, shape.back, 1)}
            {front && frame(front, shape.front, 2)}
        </div>
    );
}

// Size and gap for the step-number badge a numbered heading spawns below its photo.
const BADGE_SIZE = 60;
const BADGE_GAP = 20;

/**
 * Photo beside a stack of heading/paragraph groups, optionally closing with a
 * card, button and/or contact form. The photo stretches to the copy column's
 * height unless `imageAspect` pins the crop, which keeps back-to-back bands
 * showing photos at one consistent size. `images` replaces `image` with a
 * two-frame collage — see SplitCollage.
 */
function SplitSection({ block }) {
    const wide = useIsWide();
    const media = variantOf('split', block.variant);
    const imageOnRight = wide && block.imageSide !== 'left';
    // A leading "1. " on the first group's heading becomes a step badge
    // below the photo instead of crowding the number into the heading text.
    const stepMatch = typeof block.groups?.[0]?.heading === 'string'
        && /^(\d+)\.\s+(.+)$/.exec(block.groups[0].heading);
    const imageStyle = {
        width: '100%', objectFit: media.fit, display: 'block',
        borderRadius: typeof media.radius === 'number' ? `${media.radius}px` : media.radius,
        ...(stepMatch ? null : { order: imageOnRight ? 2 : 1 }),
        ...(media.shadow ? { boxShadow: media.shadow } : null),
        ...(media.aspect ? { aspectRatio: media.aspect } : null),
        ...(media.maxWidth ? { maxWidth: `${media.maxWidth}px`, justifySelf: 'center' } : null),
        ...(block.imageAspect
            ? { aspectRatio: block.imageAspect, alignSelf: 'center' }
            : media.stretch === false
                ? { height: 'auto', alignSelf: 'center' }
                : {
                    height: wide ? '100%' : 'auto',
                    maxHeight: wide ? 'none' : `${media.maxHeightNarrow}px`,
                }),
    };
    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.blockY}px 0 8px` }}>
            <div style={{
                ...container(WIDTH.band), display: 'grid',
                gridTemplateColumns: wide ? '1fr 1fr' : '1fr',
                gap: wide ? `${SPACE.colGap}px` : `${SPACE.groupGap}px`,
                alignItems: wide ? 'stretch' : 'center',
            }}>
                {block.images ? (
                    <SplitCollage items={block.images} order={imageOnRight ? 2 : 1} variant={block.collageVariant} />
                ) : stepMatch ? (
                    <div style={{ position: 'relative', order: imageOnRight ? 2 : 1 }}>
                        <img src={block.image} alt={block.imageAlt || ''} loading="lazy" style={imageStyle} />
                        {/* BADGE_GAP below the photo, matching the gap kept above the heading. */}
                        <span aria-hidden="true" style={{
                            position: 'absolute', top: `calc(100% + ${BADGE_GAP}px)`, left: '50%', transform: 'translate(-50%, 0)',
                            width: BADGE_SIZE, height: BADGE_SIZE, borderRadius: '50%', background: COLORS.dark, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: FONTS.heading, fontSize: 26, fontWeight: 600,
                        }}>
                            {stepMatch[1]}
                        </span>
                    </div>
                ) : (
                <img
                    src={block.image}
                    alt={block.imageAlt || ''}
                    loading="lazy"
                    style={imageStyle}
                />
                )}
                <div style={{
                    order: imageOnRight ? 1 : 2, alignSelf: 'center',
                    // Grid `gap` already inserts SPACE.groupGap above this column, so
                    // padding only needs to add back what the badge (plus its own gaps
                    // on each side) eats into that.
                    ...(stepMatch ? { paddingTop: 2 * BADGE_GAP + BADGE_SIZE - SPACE.groupGap } : null),
                }}>
                    {block.groups.map((group, i) => (
                        <div key={i} style={{ marginBottom: `${SPACE.groupGap}px` }}>
                            {group.heading && (
                                <h2 style={{
                                    ...HEADING, margin: '0 0 14px', color: COLORS.dark,
                                    // `headingNoWrap` trades font size for width to stay on one line.
                                    ...(group.headingNoWrap
                                        ? { whiteSpace: 'nowrap', fontSize: wide ? 'clamp(18px, 2.5vw, 30px)' : 'clamp(15px, 5vw, 30px)' }
                                        // `rule`: display-sized title over a hairline, sized down
                                        // with the column so it still fits when the band is two-up.
                                        : group.rule
                                            ? { fontSize: 'clamp(32px, 3.6vw, 45px)' }
                                            : { fontSize: TYPE.bandHeading }),
                                    ...(group.rule
                                        ? { borderBottom: `1px solid ${COLORS.dark}`, paddingBottom: '24px', margin: '0 0 32px' }
                                        : null),
                                    // Last word on size, for a title the shared step oversizes.
                                    ...(group.headingSize ? { fontSize: group.headingSize } : null),
                                }}>
                                    <InlineText value={i === 0 && stepMatch ? stepMatch[2] : group.heading} />
                                </h2>
                            )}
                            {group.paragraphs.map((para, j) => (
                                <p key={j} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, margin: '0 0 14px' }}>
                                    <InlineText value={para} />
                                </p>
                            ))}
                        </div>
                    ))}
                    {block.card && (
                        <ProductCard card={block.card} media={variantOf('card', block.card.variant)} />
                    )}
                    {/* Collapsed detail beside the photo — the runners plan puts
                        its description, pricing and small print here rather than
                        spilling them down the page. */}
                    {block.accordion && (
                        <div style={{ margin: `${SPACE.groupGap}px 0` }}>
                            <Accordion items={block.accordion} compact />
                        </div>
                    )}
                    {block.cta && (
                        <ActionLink
                            action={block.cta}
                            style={block.cta.variant === 'dark' ? BUTTON_DARK : BUTTON}
                        />
                    )}
                    {block.form && (
                        <div style={{ marginTop: block.cta ? `${SPACE.groupGap}px` : 0 }}>
                            <ContactFormBlock inline {...block.form} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/**
 * Full-width row of photo-above-caption steps. Column count is stepped rather
 * than auto-fit, which would leave an orphan on the last row at tablet widths.
 * Items may carry their own heading and CTA (pinned to the foot so buttons stay
 * level). `align: 'left'` gives a display-size title on the left margin;
 * `variant: 'portraits'` crops each photo to a roundel.
 */
function StepsSection({ block }) {
    const wide = useMediaQuery(MQ.row);
    const medium = useMediaQuery(MQ.stack);
    // 2-up at tablet widths only when the items divide evenly.
    const mediumColumns = block.items.length % 2 === 0 ? 2 : 1;
    const columns = Math.min(block.items.length, wide ? 4 : medium ? mediumColumns : 1);
    const alignLeft = block.align === 'left';
    const media = variantOf('steps', block.variant);
    const circle = media.radius === '50%';
    // Stacked, narrow to a readable measure instead of one enormous full-width photo.
    const containerMax = columns === 1 ? WIDTH.stack : WIDTH.full;
    return (
        // marginTop separates the tint from the preceding band, which closes
        // tight against its own bottom edge.
        <section style={{
            background: toneOf(block.tone),
            padding: `${SPACE.bandY}px 0`, marginTop: `${SPACE.bandTop}px`,
        }}>
            {block.heading && (
                <div style={container(containerMax)}>
                    <h2 style={{
                        ...HEADING, color: COLORS.dark,
                        fontSize: alignLeft ? TYPE.displayHeading : TYPE.sectionHeading,
                        margin: alignLeft ? '0 0 72px' : '0 0 44px',
                        textAlign: alignLeft ? 'left' : 'center',
                    }}>
                        {block.heading}
                    </h2>
                </div>
            )}
            <div style={{
                ...container(containerMax), display: 'grid', gap: `${SPACE.blockY}px`,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}>
                {block.items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column' }}>
                        <img
                            src={item.image}
                            alt={item.imageAlt || ''}
                            loading="lazy"
                            style={{
                                objectFit: 'cover', display: 'block',
                                // Geometry, including the roundel inset and size cap,
                                // lives in MEDIA.steps.
                                width: media.width,
                                ...(media.maxWidth ? { maxWidth: `${media.maxWidth}px` } : null),
                                aspectRatio: block.imageAspect || media.aspect,
                                borderRadius: typeof media.radius === 'number' ? `${media.radius}px` : media.radius,
                                marginBottom: `${media.gapBelow}px`,
                            }}
                        />
                        {item.heading && (
                            <h3 style={{
                                ...HEADING, fontSize: TYPE.cardTitle, lineHeight: 1.3,
                                margin: '0 0 12px', color: COLORS.dark,
                                // Under roundels, titles share a baseline so a one-line
                                // title doesn't float above its neighbours.
                                ...(circle ? { display: 'flex', alignItems: 'flex-end', minHeight: '2.6em' } : null),
                            }}>
                                {item.heading}
                            </h3>
                        )}
                        <p style={{ fontSize: TYPE.body, lineHeight: 1.7, color: COLORS.text, margin: 0 }}>
                            {item.text}
                        </p>
                        {item.cta && (
                            <div style={{ marginTop: 'auto', paddingTop: '22px' }}>
                                <ActionLink action={item.cta} style={BUTTON} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </section>
    );
}

// Purchase card: photo, title, price, lines and a CTA pinned to the foot so
// buttons stay level across cards. `chrome: false` drops the white panel.
function ProductCard({ card, media = MEDIA.card.default }) {
    const chrome = media.chrome !== false;
    return (
        <div style={{
            textAlign: media.align || 'center', display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            ...(chrome ? {
                background: SURFACE.cardBg, border: SURFACE.rule, borderRadius: `${RADIUS.band}px`,
                padding: card.image ? `0 0 ${SPACE.groupGap}px` : `${SPACE.groupGap}px 24px`,
            } : null),
        }}>
            {card.image && (
                <img
                    src={card.image}
                    alt={card.imageAlt || ''}
                    loading="lazy"
                    style={{
                        width: '100%', aspectRatio: media.aspect, objectFit: 'cover',
                        display: 'block', marginBottom: '24px',
                        ...(chrome ? null : { borderRadius: `${RADIUS.tile}px` }),
                    }}
                />
            )}
            <div style={{
                padding: card.image && chrome ? '0 24px' : 0,
                display: 'flex', flexDirection: 'column', flex: 1,
            }}>
                {card.title && (
                    <h3 style={{ ...HEADING, fontSize: TYPE.cardTitle, margin: '0 0 6px', color: COLORS.dark }}>
                        {card.title}
                    </h3>
                )}
                {card.price && (
                    <div style={{ fontSize: TYPE.cardPrice, fontWeight: 700, color: COLORS.dark, margin: '0 0 14px' }}>
                        {card.price}
                    </div>
                )}
                {(card.lines || []).map((line, j) => (
                    <p key={j} style={{ fontSize: TYPE.bodySm, lineHeight: 1.6, color: COLORS.text, margin: '0 0 8px' }}>
                        {line}
                    </p>
                ))}
                {card.cta && (
                    <div style={{ marginTop: 'auto', paddingTop: '18px' }}>
                        <ActionLink action={card.cta} style={BUTTON} />
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Running copy sharing one row with the cards it describes, so the buy buttons
 * stay on screen while the copy is read. Below 1000px the copy takes full width
 * with the cards 2-up beneath; below 640px everything stacks. The cards sit in
 * their own nested grid so they match each other's height, not the copy's.
 */
function TextCardsSection({ block }) {
    const wide = useMediaQuery(MQ.wide);
    const twoUp = useMediaQuery(MQ.pair);
    const media = variantOf('card', block.variant);
    const copy = block.copy || {};
    const CopyList = copy.ordered ? 'ol' : 'ul';
    // One card must not sit in a half-width column with an empty cell beside it.
    const cardColumns = Math.min(block.items.length, 2);

    const headings = block.items.map((card, i) => (
        <h3 key={`h${i}`} style={{
            ...HEADING, fontSize: TYPE.subHeading, margin: '0 0 16px',
            color: COLORS.dark, textAlign: 'center',
        }}>
            {card.heading}
        </h3>
    ));
    const cards = block.items.map((card, i) => <ProductCard key={`c${i}`} card={card} media={media} />);

    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.blockY}px 0` }}>
            <div style={{
                ...container(WIDTH.wide), display: 'grid',
                gridTemplateColumns: wide ? 'minmax(0, 1.05fr) minmax(0, 2fr)' : '1fr',
                gap: wide ? '44px' : '36px', alignItems: 'start',
            }}>
                <div>
                    {copy.heading && (
                        <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 16px', color: COLORS.dark }}>
                            {copy.heading}
                        </h2>
                    )}
                    {(copy.paragraphs || []).map((para, i) => (
                        <p key={i} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, margin: '0 0 16px' }}>
                            <InlineText value={para} />
                        </p>
                    ))}
                    {copy.list && (
                        <CopyList style={{ margin: '0 0 16px', paddingLeft: '24px' }}>
                            {copy.list.map((item, i) => (
                                <li key={i} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, marginBottom: '8px' }}>
                                    <InlineText value={item} />
                                </li>
                            ))}
                        </CopyList>
                    )}
                </div>
                <div style={{
                    display: 'grid', gap: '20px',
                    gridTemplateColumns: twoUp ? `repeat(${cardColumns}, minmax(0, 1fr))` : '1fr',
                    // Cap the card width: full-column would draw a photo half the
                    // viewport high. Stacked it centres; beside copy it sits left.
                    ...(twoUp
                        ? {
                            gridTemplateRows: 'auto 1fr',
                            ...(cardColumns === 1 ? { maxWidth: `${WIDTH.card}px` } : null),
                        }
                        : { maxWidth: `${WIDTH.card}px`, width: '100%', margin: '0 auto' }),
                }}>
                    {/* Row-major so the cards start level even when a heading wraps;
                        stacked, the pairs have to interleave. */}
                    {twoUp
                        ? [...headings, ...cards]
                        : block.items.flatMap((card, i) => [headings[i], cards[i]])}
                </div>
            </div>
        </section>
    );
}

/**
 * Prose columns side by side, each under its own ruled heading. A column may
 * carry a small image (a partner logo) beside its copy. `listRow`'s prose
 * sibling: paragraphs instead of bulleted lists.
 */
function ColumnsSection({ block }) {
    const wide = useIsWide();
    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.blockY}px 0` }}>
            <div style={{
                ...container(WIDTH.wide), display: 'grid',
                gridTemplateColumns: wide ? `repeat(${block.items.length}, minmax(0, 1fr))` : '1fr',
                gap: wide ? `${SPACE.colGap}px` : `${SPACE.bandTop}px`, alignItems: 'start',
            }}>
                {block.items.map((column, i) => {
                    const copy = (column.paragraphs || []).map((para, j) => (
                        <p key={j} style={{
                            fontSize: column.size === 'lg' ? TYPE.bodyLg : TYPE.body,
                            lineHeight: 1.75, color: COLORS.text, margin: '0 0 14px',
                        }}>
                            <InlineText value={para} />
                        </p>
                    ));
                    return (
                        <div key={i}>
                            {column.heading && (
                                <h2 style={{
                                    ...HEADING, color: COLORS.dark,
                                    fontSize: column.size === 'lg' ? TYPE.sectionHeadingLg : TYPE.sectionHeading,
                                    margin: column.rule ? '0 0 32px' : '0 0 16px',
                                    ...(column.bold ? { fontWeight: 700 } : null),
                                    ...(column.rule
                                        ? { borderBottom: `1px solid ${COLORS.dark}`, paddingBottom: '24px' }
                                        : null),
                                }}>
                                    {column.heading}
                                </h2>
                            )}
                            {column.image ? (
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <img
                                        src={column.image}
                                        alt={column.imageAlt || ''}
                                        loading="lazy"
                                        style={{ width: '190px', maxWidth: '100%', display: 'block' }}
                                    />
                                    <div style={{ flex: '1 1 200px' }}>{copy}</div>
                                </div>
                            ) : copy}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

/**
 * Copy set inside organic amber blobs (cycled 8-value radii from SHAPE.blobs).
 * Rows are built explicitly and centred so a short final row sits centred under
 * a full one rather than ranged left.
 */
function ShapesSection({ block }) {
    // The panels are a fixed size, so capacity is arithmetic rather than a
    // guessed breakpoint, which would wrap a row mid-group.
    const rowWidth = (n) => n * SHAPE.size + (n - 1) * SHAPE.gap + PAD * 2;
    const fitsThree = useMediaQuery(`(min-width: ${rowWidth(3)}px)`);
    const fitsTwo = useMediaQuery(`(min-width: ${rowWidth(2)}px)`);
    const perRow = Math.min(block.items.length, fitsThree ? 3 : fitsTwo ? 2 : 1);
    const rows = [];
    for (let i = 0; i < block.items.length; i += perRow) rows.push(block.items.slice(i, i + perRow));

    return (
        <section style={{ background: toneOf(block.tone), padding: `8px 0 ${SPACE.bandY}px` }}>
            <div style={container(WIDTH.full)}>
                {rows.map((row, r) => (
                    <div key={r} style={{
                        display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
                        gap: `${SHAPE.gap}px`, marginBottom: `${SHAPE.gap}px`,
                    }}>
                        {row.map((item, c) => (
                            // Square by aspect-ratio with the copy out of the flow:
                            // letting content size the box stretched these into
                            // ellipses.
                            <div key={c} style={{
                                flex: '0 0 auto', width: `${SHAPE.size}px`, maxWidth: '86vw',
                                aspectRatio: '1 / 1', background: SHAPE.fill, position: 'relative',
                                borderRadius: SHAPE.blobs[(r * perRow + c) % SHAPE.blobs.length],
                            }}>
                                {/* `inset` gives the copy a definite box, so the
                                    measure can never collapse. */}
                                <div style={{
                                    position: 'absolute', inset: `${SHAPE.padY} ${SHAPE.padX}`,
                                    display: 'flex', flexDirection: 'column',
                                    justifyContent: 'center', textAlign: 'center',
                                }}>
                                    {item.heading && (
                                        <h3 style={{
                                            ...HEADING, fontSize: TYPE.cardTitle, letterSpacing: '1px',
                                            color: COLORS.dark, margin: '0 0 12px',
                                        }}>
                                            {item.heading}
                                        </h3>
                                    )}
                                    {item.lines.map((line, j) => (
                                        <p key={j} style={{
                                            fontSize: TYPE.bodySm, lineHeight: 1.45, color: COLORS.dark,
                                            margin: j === item.lines.length - 1 ? 0 : '0 0 8px',
                                        }}>
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
}

// Short lists side by side so they can be read across rather than by scrolling.
// Stacks below 1000px, where three text columns leave no readable line length.
function ListRow({ block }) {
    const wide = useMediaQuery(MQ.wide);
    const ListTag = block.ordered ? 'ol' : 'ul';
    return (
        <section style={{ background: toneOf(block.tone), padding: '8px 0 24px' }}>
            <div style={{
                ...container(WIDTH.wide), display: 'grid',
                gridTemplateColumns: wide ? `repeat(${block.items.length}, minmax(0, 1fr))` : '1fr',
                gap: wide ? `${SPACE.blockY}px` : '0', alignItems: 'start',
            }}>
                {block.items.map((column, i) => (
                    <div key={i}>
                        {column.heading && (
                            <h3 style={{ ...HEADING, fontSize: TYPE.subHeading, margin: '28px 0 12px', color: COLORS.dark }}>
                                {column.heading}
                            </h3>
                        )}
                        <ListTag style={{ margin: '0 0 16px', paddingLeft: '24px' }}>
                            {column.items.map((item, j) => (
                                <li key={j} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, marginBottom: '8px' }}>
                                    {item}
                                </li>
                            ))}
                        </ListTag>
                    </div>
                ))}
            </div>
        </section>
    );
}

/**
 * `variant: 'bleed'`: portrait frames butted edge to edge, running off both
 * sides of the screen. Frames are a fixed size rather than a share of the
 * viewport, which would blow them up past 500px on a wide monitor.
 *
 * The row is measured rather than sized in CSS, because a fixed tile width
 * silently hid photos: five 280px frames need 1400px, so on any laptop below
 * that the end frames were cropped away behind a hidden scrollbar. Tiles now
 * shrink to share the row, down to `minTile` — past that the row genuinely
 * cannot hold them all (phones), and scrolling is the right answer. Overflow
 * alignment then flips: an overflowing row scrolls to its midpoint, while
 * `justify-content: center` would push its head past the scroll origin where
 * it can't be scrolled back into view.
 */
function BleedGallery({ block }) {
    const scroller = useRef(null);
    const [fits, setFits] = useState(false);
    const media = MEDIA.gallery.bleed;
    const [tile, setTile] = useState(media.tile);

    useEffect(() => {
        const el = scroller.current;
        if (!el) return undefined;
        const sync = () => {
            const share = el.clientWidth / block.items.length;
            setTile(Math.max(media.minTile, Math.min(media.tile, Math.floor(share))));
            // Read overflow after the browser has applied the new width.
            requestAnimationFrame(() => {
                const overflows = el.scrollWidth > el.clientWidth + 1;
                setFits(!overflows);
                if (overflows) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
            });
        };
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, [block.items.length, media.tile, media.minTile]);

    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.bandY}px 0 0` }}>
            {block.heading && (
                <div style={container(WIDTH.full)}>
                    <h2 style={{
                        ...HEADING, fontSize: TYPE.displayHeading,
                        margin: `0 0 ${SPACE.bandY}px`, color: COLORS.dark,
                    }}>
                        {block.heading}
                    </h2>
                </div>
            )}
            <div ref={scroller} style={{
                display: 'flex', overflowX: 'auto', scrollbarWidth: 'none',
                justifyContent: fits ? 'center' : 'flex-start',
            }}>
                {block.items.map((item, i) => (
                    <img
                        key={i}
                        src={item.image}
                        alt={item.imageAlt || ''}
                        loading="lazy"
                        style={{
                            // Longhands, not `flex: 0 0 min(...)`: as a shorthand the
                            // whole declaration drops where min() won't parse, leaving
                            // photos at their intrinsic size (up to 1500px here).
                            flex: '0 0 auto',
                            width: `${tile}px`, maxWidth: media.maxWidth,
                            aspectRatio: block.imageAspect || media.aspect,
                            objectFit: 'cover', display: 'block',
                        }}
                    />
                ))}
            </div>
        </section>
    );
}

// The pitch on the left, the form on the right; below 900px it stacks copy
// first so the ask still precedes the fields.
function FormBand({ block }) {
    const wide = useMediaQuery(MQ.form);
    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.bandY}px 0` }}>
            <div style={{
                ...container(WIDTH.wide), display: 'grid',
                gridTemplateColumns: wide ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                gap: wide ? `${SPACE.bandY}px` : `${SPACE.blockY}px`, alignItems: 'center',
            }}>
                {/* Ranged left, so the pitch shares a margin with the form beside it. */}
                <div>
                    {block.heading && (
                        <h2 style={{
                            ...HEADING, fontSize: TYPE.displayHeading, margin: '0 0 32px',
                            color: COLORS.dark,
                        }}>
                            {block.heading}
                        </h2>
                    )}
                    {(block.paragraphs || []).map((para, i) => (
                        <p key={i} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, margin: '0 0 20px' }}>
                            <InlineText value={para} />
                        </p>
                    ))}
                </div>
                <ContactFormBlock inline {...block.form} />
            </div>
        </section>
    );
}

function GallerySection({ block }) {
    const media = variantOf('gallery', block.variant);
    // Capacity is measured against a full-size row, not this block's own tiles,
    // so `dense` shrinks its photos instead of narrowing the whole section.
    const full = MEDIA.gallery.default;
    // One query per candidate count, largest first: a fixed number of hooks with
    // ascending thresholds, so exactly one picks the capacity.
    const fitsSeven = useMediaQuery(`(min-width: ${galleryRowWidth(7, full)}px)`);
    const fitsSix = useMediaQuery(`(min-width: ${galleryRowWidth(6, full)}px)`);
    const fitsFive = useMediaQuery(`(min-width: ${galleryRowWidth(5, full)}px)`);
    const fitsFour = useMediaQuery(`(min-width: ${galleryRowWidth(4, full)}px)`);
    const fitsThree = useMediaQuery(`(min-width: ${galleryRowWidth(3, full)}px)`);
    const capacity = fitsSeven ? full.maxColumns
        : fitsSix ? 6 : fitsFive ? 5 : fitsFour ? 4 : fitsThree ? 3 : 2;
    const perRow = Math.min(block.items.length, capacity);
    const rowWidth = galleryRowWidth(perRow, full);
    const { tile, gap } = media;
    const single = block.items.length === 1;

    const heading = block.heading && (
        <h2 style={{
            ...HEADING, fontSize: TYPE.sectionHeading, margin: '0 0 36px', color: COLORS.dark,
            textAlign: 'center',
        }}>
            {block.heading}
        </h2>
    );

    if (single) {
        return (
            <section style={{ background: toneOf(block.tone), padding: `${SPACE.blockY}px 0` }}>
                {heading}
                <div style={container(WIDTH.single)}>
                    <img
                        src={block.items[0].image}
                        alt={block.items[0].imageAlt || ''}
                        loading="lazy"
                        style={{
                            width: '100%', height: 'auto', display: 'block',
                            borderRadius: `${media.radius}px`,
                        }}
                    />
                </div>
            </section>
        );
    }

    return (
        <section style={{ background: toneOf(block.tone), padding: `${SPACE.blockY}px 0` }}>
            {heading}
            <div style={{
                ...container(rowWidth), display: 'flex', gap: `${gap}px`,
                // Centering an overflowing row pushes its first tile past the
                // scroll origin, where it can't be scrolled back into view.
                justifyContent: block.items.length > perRow ? 'flex-start' : 'center',
                overflowX: 'auto', scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
            }}>
                {block.items.map((item, i) => (
                    <img
                        key={i}
                        src={item.image}
                        alt={item.imageAlt || ''}
                        loading="lazy"
                        style={{
                            // Full size, or an equal share of the row when too narrow.
                            // No shrink: seven photos on a phone would squeeze to
                            // slivers instead of scrolling. Longhands rather than a
                            // `flex: 0 0 min(...)` shorthand, which drops entirely where
                            // min() won't parse, leaving photos at intrinsic size.
                            flex: '0 0 auto',
                            width: `${tile}px`,
                            maxWidth: `calc((100% - ${(perRow - 1) * gap}px) / ${perRow})`,
                            // `imageAspect` letterboxes the tiles without narrowing them.
                            aspectRatio: block.imageAspect || media.aspect, objectFit: 'cover',
                            borderRadius: `${media.radius}px`, display: 'block',
                            scrollSnapAlign: 'start',
                        }}
                    />
                ))}
            </div>
        </section>
    );
}

// Testimonial: a two-column band with a portrait (attribution above the quote),
// or a centered pull-quote without. `variant: 'portrait'` crops it to a disc.
function Quote({ block }) {
    const wide = useIsWide();
    const media = variantOf('quote', block.variant);

    if (!block.image) {
        return (
            <div style={{
                background: toneOf(block.tone, 'warm'), padding: `48px ${PAD}px`, margin: `${SPACE.blockY}px 0`,
            }}>
                <blockquote style={{ maxWidth: `${WIDTH.measure}px`, margin: '0 auto', textAlign: 'center' }}>
                    <p style={{
                        ...HEADING, fontSize: TYPE.quote, lineHeight: 1.7, fontStyle: 'italic',
                        color: COLORS.dark, margin: '0 0 16px',
                    }}>
                        “{block.text}”
                    </p>
                    <footer style={{ fontSize: TYPE.bodySm, fontWeight: 700, color: COLORS.text }}>— {block.name}</footer>
                </blockquote>
            </div>
        );
    }

    return (
        <section style={{ padding: '48px 0' }}>
            <div style={{
                ...container(WIDTH.quote), display: 'grid',
                gridTemplateColumns: wide ? `minmax(0, ${media.column}px) 1fr` : '1fr',
                gap: wide ? '52px' : '24px', alignItems: 'center',
            }}>
                <img
                    src={block.image}
                    alt={block.imageAlt || block.name}
                    loading="lazy"
                    style={{
                        width: '100%', display: 'block',
                        borderRadius: typeof media.radius === 'number' ? `${media.radius}px` : media.radius,
                        ...(media.aspect ? { aspectRatio: media.aspect, objectFit: 'cover' } : null),
                    }}
                />
                <blockquote style={{ margin: 0 }}>
                    <div style={{ ...HEADING, fontSize: TYPE.quoteName, lineHeight: 1.4, color: COLORS.dark, marginBottom: '18px' }}>
                        {block.name}
                        {block.link && (
                            <>
                                {' '}
                                <a
                                    href={block.link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: COLORS.dark, textDecoration: 'underline' }}
                                >
                                    {block.link.label}
                                </a>
                            </>
                        )}
                    </div>
                    <p style={{ fontSize: TYPE.body, lineHeight: 1.8, fontStyle: 'italic', color: COLORS.text, margin: 0 }}>
                        “{block.text}”
                    </p>
                </blockquote>
            </div>
        </section>
    );
}

/** One arrow glyph, mirrored for "previous". */
function CarouselArrow({ back }) {
    return (
        <svg
            width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            style={{ transform: back ? 'scaleX(-1)' : 'none' }}
        >
            <path
                d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round"
            />
        </svg>
    );
}

const CAROUSEL_BUTTON = {
    width: '56px', height: '56px', flexShrink: 0, borderRadius: '50%', border: 'none',
    background: COLORS.dark, color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// Testimonials switched by arrows rather than stacked. Every quote renders into
// the same grid cell with the inactive ones hidden, so the section holds the
// height of the longest and paging doesn't make the page jump.
function QuoteCarousel({ block }) {
    const wide = useIsWide();
    const [index, setIndex] = useState(0);
    const count = block.items.length;
    const go = (step) => setIndex((i) => (i + step + count) % count);

    const arrow = (step, label, back) => (
        <button type="button" onClick={() => go(step)} aria-label={label} style={CAROUSEL_BUTTON}>
            <CarouselArrow back={back} />
        </button>
    );

    return (
        <section
            style={{ background: toneOf(block.tone), padding: `${SPACE.bandY}px 0` }}
            aria-roledescription="carousel"
            aria-label={block.heading || 'Testimonials'}
        >
            {block.heading && (
                <div style={container(WIDTH.wide)}>
                    <h2 style={{
                        ...HEADING, fontSize: TYPE.sectionHeading, color: COLORS.dark,
                        textAlign: 'center', margin: `0 0 ${SPACE.bandTop}px`,
                    }}>
                        {block.heading}
                    </h2>
                </div>
            )}
            <div style={{
                ...container(WIDTH.wide), display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: wide ? '40px' : '20px',
                flexDirection: wide ? 'row' : 'column-reverse',
            }}>
                {wide && arrow(-1, 'Previous testimonial', true)}
                <div style={{ display: 'grid', flex: '1 1 auto', maxWidth: `${WIDTH.measure}px` }}>
                    {block.items.map((item, i) => (
                        <blockquote
                            key={i}
                            // Same cell for every quote: the box takes the height of
                            // the tallest, so switching never reflows the page.
                            style={{
                                gridArea: '1 / 1', margin: 0, textAlign: 'center',
                                visibility: i === index ? 'visible' : 'hidden',
                            }}
                            aria-hidden={i !== index}
                        >
                            <p style={{
                                ...HEADING, fontSize: TYPE.quote, lineHeight: 1.6,
                                color: COLORS.dark, margin: '0 0 24px',
                            }}>
                                {item.text}
                            </p>
                            <footer style={{ fontSize: TYPE.bodySm, fontWeight: 700, color: COLORS.text }}>
                                {item.name}
                            </footer>
                        </blockquote>
                    ))}
                </div>
                {wide ? arrow(1, 'Next testimonial', false) : (
                    <div style={{ display: 'flex', gap: '20px' }}>
                        {arrow(-1, 'Previous testimonial', true)}
                        {arrow(1, 'Next testimonial', false)}
                    </div>
                )}
            </div>
            {/* Announced to screen readers on change; the quotes themselves are
                hidden except the active one, so this carries the position. */}
            <p aria-live="polite" style={{
                textAlign: 'center', fontSize: TYPE.bodySm, color: COLORS.muted,
                margin: `${SPACE.groupGap}px 0 0`,
            }}>
                {index + 1} of {count}
            </p>
        </section>
    );
}

const FAQ_RULE = SURFACE.rule;

// A square rotated 45° showing only its right and bottom borders, so the
// arrowhead spans the full 14px box. Flips up while the row is open.
function FaqChevron({ open }) {
    return (
        <span
            aria-hidden="true"
            style={{
                width: '14px', height: '14px', flexShrink: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: COLORS.dark,
                transform: open ? 'rotate(-180deg)' : 'none', transition: 'transform 0.25s',
            }}
        >
            <span style={{
                width: '70.71%', height: '70.71%', boxSizing: 'border-box',
                borderStyle: 'solid', borderColor: 'currentColor', borderWidth: '3px',
                borderTop: 'none', borderLeft: 'none',
                transform: 'translateY(-25%) rotate(45deg)',
            }} />
        </span>
    );
}

const FAQ_LINK = { color: COLORS.dark, textDecoration: 'underline', fontWeight: 600 };

// A plain string, or an array of strings interleaved with {bold} and
// {label, to|href} link segments.
function InlineText({ value }) {
    if (typeof value === 'string') return value;
    return value.map((segment, i) => {
        if (typeof segment === 'string') return <Fragment key={i}>{segment}</Fragment>;
        if (segment.bold) return <strong key={i}>{segment.bold}</strong>;
        // `mark` rules a word in amber — the legacy pages' hand-drawn underline.
        if (segment.mark) return <span key={i} style={MARK}>{segment.mark}</span>;
        return <ActionLink key={i} action={segment} style={FAQ_LINK} />;
    });
}

// Paragraphs, plus entries given as {list} or {cta} so a download stays inside
// the answer that offers it.
function FaqAnswer({ parts }) {
    const wide = useIsWide();
    return (
        <div style={{ maxWidth: wide ? '70%' : '100%', paddingBottom: '30px' }}>
            {parts.map((part, i) => {
                if (part.cta) {
                    return <ActionLink key={i} action={part.cta} style={{ ...BUTTON, margin: '4px 0 6px' }} />;
                }
                if (part.list) {
                    const ListTag = part.ordered ? 'ol' : 'ul';
                    return (
                        <ListTag key={i} style={{ margin: '0 0 14px', paddingLeft: '22px' }}>
                            {part.list.map((entry, j) => (
                                <li key={j} style={{ fontSize: TYPE.body, lineHeight: 1.7, color: COLORS.text, marginBottom: '6px' }}>
                                    <InlineText value={entry} />
                                </li>
                            ))}
                        </ListTag>
                    );
                }
                return (
                    <p key={i} style={{ fontSize: TYPE.body, lineHeight: 1.7, color: COLORS.text, margin: '0 0 14px' }}>
                        <InlineText value={part} />
                    </p>
                );
            })}
        </div>
    );
}

function FaqItem({ item, open, onToggle, last, compact = false }) {
    return (
        <div style={{ borderTop: FAQ_RULE, ...(last ? { borderBottom: FAQ_RULE } : null) }}>
            <button
                onClick={onToggle}
                aria-expanded={open}
                style={{
                    width: '100%', textAlign: 'left', background: 'none', border: 'none',
                    padding: compact ? '16px 0' : '30px 0', cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', gap: '16px',
                }}
            >
                {/* Inside a column the rows are tighter and set in the sans face,
                    so the accordion reads as a control rather than as a run of
                    section headings. */}
                <span style={{
                    fontFamily: compact ? FONTS.sans : FONTS.serif,
                    fontSize: compact ? TYPE.bodySm : TYPE.faqQuestion, lineHeight: 1.35,
                    color: COLORS.dark, flex: 1,
                }}>
                    {item.q}
                </span>
                <FaqChevron open={open} />
            </button>
            {open && <FaqAnswer parts={item.a} />}
        </div>
    );
}

// Only one row is open at a time. No container of its own, so it can sit inside
// a `split` column as well as on its own at the reading measure.
function Accordion({ items, compact = false }) {
    const [openIndex, setOpenIndex] = useState(null);
    return items.map((item, i) => (
        <FaqItem
            key={i}
            item={item}
            compact={compact}
            open={openIndex === i}
            last={i === items.length - 1}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
        />
    ));
}

function FaqBlock({ block }) {
    return (
        <div style={{ ...WRAP, marginBottom: '48px' }}>
            <Accordion items={block.items} />
        </div>
    );
}

/**
 * A word scrolling edge to edge at display size — the legacy site's page-topping
 * band. The phrase is repeated to twice the track width and translated by half,
 * so the loop is seamless. Decorative, so it is hidden from assistive tech; the
 * animation lives in index.css because inline styles can't hold @keyframes.
 */
function Marquee({ block }) {
    const phrase = `${block.text}   •   `;
    const half = phrase.repeat(5);
    return (
        <section aria-hidden="true" style={{
            background: toneOf(block.tone), overflow: 'hidden', padding: '18px 0',
            marginBottom: `${SPACE.blockY}px`,
        }}>
            <div className="sf-marquee-track">
                {[0, 1].map((i) => (
                    <span key={i} style={{
                        ...HEADING, fontSize: TYPE.displayHeading, color: COLORS.dark,
                        whiteSpace: 'pre', lineHeight: 1.2,
                    }}>
                        {half}
                    </span>
                ))}
            </div>
        </section>
    );
}

function Block({ block }) {
    switch (block.type) {
        case 'marquee': return <Marquee block={block} />;

        // `level` steps the heading down for pages that group content two deep.
        case 'heading': {
            const Tag = block.level === 'sub' ? 'h3' : 'h2';
            const size = block.level === 'sub' ? TYPE.subHeading
                : block.level === 'band' ? TYPE.bandHeading
                    : TYPE.sectionHeading;
            return (
                // `rule` hangs a hairline under the title, wider than the copy.
                <div style={block.rule ? container(WIDTH.narrow) : WRAP}>
                    <Tag style={{
                        ...HEADING, fontSize: size, margin: '40px 0 16px', color: COLORS.dark,
                        ...(block.align === 'center' ? { textAlign: 'center' } : null),
                        ...(block.rule
                            ? { borderBottom: `1px solid ${COLORS.dark}`, paddingBottom: '20px', margin: '40px 0 32px' }
                            : null),
                    }}>
                        {block.text}
                    </Tag>
                </div>
            );
        }

        case 'text': {
            // `accent` is the amber line at body size; `display` is the same line
            // blown up to display size on its own band, for a page's one big
            // human sentence.
            const accent = block.variant === 'accent';
            const display = block.variant === 'display';
            return (
                <section style={{
                    background: toneOf(block.tone), padding: display ? `${SPACE.bandY}px 0` : 0,
                }}>
                    {/* `wide` lines the copy up with the wider bands around it. */}
                    <div style={{
                        ...(block.wide ? container(WIDTH.wide) : WRAP),
                        ...(display ? { textAlign: 'center' } : null),
                    }}>
                        {block.heading && (
                            <h3 style={{ ...HEADING, fontSize: TYPE.subHeading, margin: '28px 0 12px', color: COLORS.dark }}>
                                {block.heading}
                            </h3>
                        )}
                        {block.paragraphs.map((para, i) => (
                            <p key={i} style={{
                                ...(display ? HEADING : null),
                                fontSize: display ? TYPE.displayHeading
                                    : block.size === 'lg' ? TYPE.bodyLg : TYPE.body,
                                lineHeight: display ? 1.25 : 1.75, margin: '0 0 16px',
                                color: accent || display ? COLORS.orange : COLORS.text,
                            }}>
                                <InlineText value={para} />
                            </p>
                        ))}
                    </div>
                </section>
            );
        }

        case 'list': {
            const ListTag = block.ordered ? 'ol' : 'ul';
            return (
                <div style={WRAP}>
                    {block.heading && (
                        <h3 style={{ ...HEADING, fontSize: TYPE.subHeading, margin: '28px 0 12px', color: COLORS.dark }}>
                            {block.heading}
                        </h3>
                    )}
                    <ListTag style={{ margin: '0 0 16px', paddingLeft: '24px' }}>
                        {block.items.map((item, i) => (
                            <li key={i} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, marginBottom: '8px' }}>
                                {item}
                            </li>
                        ))}
                    </ListTag>
                </div>
            );
        }

        case 'faq':
            return <FaqBlock block={block} />;

        case 'imageText': {
            const img = (
                <div style={{
                    minHeight: `${MEDIA.imageText.minHeight}px`, backgroundImage: `url(${block.image})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    borderRadius: `${MEDIA.imageText.radius}px`,
                }} />
            );
            const copy = (
                <div>
                    {block.heading && (
                        <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 14px', color: COLORS.dark }}>
                            {block.heading}
                        </h2>
                    )}
                    {block.paragraphs.map((para, i) => (
                        <p key={i} style={{ fontSize: TYPE.body, lineHeight: 1.75, color: COLORS.text, margin: '0 0 14px' }}>
                            {para}
                        </p>
                    ))}
                </div>
            );
            return (
                <div style={{
                    ...WRAP, maxWidth: `${WIDTH.narrow}px`, display: 'grid', gap: '36px',
                    alignItems: 'center', padding: `32px ${PAD}px`,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                }}>
                    {block.imageSide === 'left' ? <>{img}{copy}</> : <>{copy}{img}</>}
                </div>
            );
        }

        case 'quote':
            return <Quote block={block} />;

        case 'quotes':
            return <QuoteCarousel block={block} />;

        case 'formBand':
            return <FormBand block={block} />;

        case 'split':
            return <SplitSection block={block} />;

        case 'steps':
            return <StepsSection block={block} />;

        case 'gallery':
            return block.variant === 'bleed'
                ? <BleedGallery block={block} />
                : <GallerySection block={block} />;

        case 'textCards':
            return <TextCardsSection block={block} />;

        case 'listRow':
            return <ListRow block={block} />;

        case 'columns':
            return <ColumnsSection block={block} />;

        case 'shapes':
            return <ShapesSection block={block} />;

        case 'cards':
            return (
                <>
                    {/* `align: 'left'` anchors the title to the page's left margin at
                        display size — the same treatment as a bleed gallery's title,
                        so consecutive sections share one left edge. */}
                    {block.heading && (block.align === 'left' ? (
                        <div style={container(WIDTH.full)}>
                            <h2 style={{
                                ...HEADING, fontSize: TYPE.displayHeading,
                                margin: `36px 0 ${SPACE.blockY}px`, color: COLORS.dark,
                            }}>
                                {block.heading}
                            </h2>
                        </div>
                    ) : (
                        <div style={{ ...WRAP, maxWidth: `${WIDTH.narrow}px` }}>
                            <h2 style={{ ...HEADING, fontSize: TYPE.sectionHeading, margin: '36px 0 20px', color: COLORS.dark, textAlign: 'center' }}>
                                {block.heading}
                            </h2>
                        </div>
                    ))}
                    <div style={{ ...WRAP, maxWidth: `${WIDTH.narrow}px` }}>
                        <div style={{
                            display: 'grid', gap: '20px', margin: '0 0 20px',
                            gridTemplateColumns: `repeat(auto-fit, minmax(260px, ${block.items.length === 1 ? `${WIDTH.card}px` : '1fr'}))`,
                            justifyContent: 'center',
                        }}>
                            {block.items.map((card, i) => (
                                <ProductCard key={i} card={card} media={variantOf('card', block.variant)} />
                            ))}
                        </div>
                    </div>
                </>
            );

        // Like 'cards', but each card navigates.
        case 'linkCards':
            return (
                <div style={{ ...WRAP, maxWidth: `${WIDTH.narrow}px` }}>
                    {block.heading && (
                        <h2 style={{ ...HEADING, fontSize: TYPE.sectionHeading, margin: '36px 0 20px', color: COLORS.dark, textAlign: 'center' }}>
                            {block.heading}
                        </h2>
                    )}
                    <div style={{
                        display: 'grid', gap: '16px', margin: '0 0 20px',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    }}>
                        {block.items.map((card) => (
                            <Link
                                key={card.to}
                                to={card.to}
                                style={{
                                    background: SURFACE.cardBg, border: SURFACE.rule,
                                    borderRadius: `${RADIUS.band}px`,
                                    padding: '22px 20px', textDecoration: 'none', display: 'block',
                                }}
                            >
                                <h3 style={{ ...HEADING, fontSize: TYPE.cardTitle, margin: '0 0 6px', color: COLORS.dark }}>
                                    {card.title}
                                </h3>
                                {card.blurb && (
                                    <p style={{ fontSize: TYPE.bodySm, lineHeight: 1.6, color: COLORS.text, margin: 0 }}>
                                        {card.blurb}
                                    </p>
                                )}
                            </Link>
                        ))}
                    </div>
                </div>
            );

        case 'cta':
            return <Cta block={block} />;

        case 'ctaGroup':
            return <CtaGroup block={block} />;

        case 'form':
            return (
                <ContactFormBlock
                    formType={block.formType}
                    heading={block.heading}
                    intro={block.intro}
                    fields={block.fields}
                    submitLabel={block.submitLabel}
                />
            );

        default:
            return null;
    }
}

// `page` lets a wrapper hand in a page object built at runtime (FaqsPage
// assembles one from Airtable); without it the slug in the URL picks one
// from content/pages as before.
export default function ContentPage({ page: pageProp = null }) {
    const { slug } = useParams();
    const page = pageProp || PAGES[slug];

    useEffect(() => {
        if (page) document.title = `${page.title} — Square Fare`;
    }, [page]);

    if (!page) return <NotFoundPage />;

    // The subtitle defaults to an eyebrow ABOVE the title: on the condition
    // pages it opens a sentence the title completes, so it can't move globally.
    // `subtitleBelow` puts it under the title instead.
    const heroSubtitle = (below, color) => page.hero.subtitle && (
        <p style={below
            ? { fontSize: TYPE.body, lineHeight: 1.6, maxWidth: `${WIDTH.measure}px`, margin: '18px auto 0', color }
            : { fontSize: TYPE.heroEyebrow, letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 10px', color }}
        >
            {page.hero.subtitle}
        </p>
    );

    const below = page.hero?.subtitleBelow;
    // `align: 'left'` anchors the title to the left edge at display size.
    const heroLeft = page.hero?.align === 'left';

    return (
        // No bottom padding: every page closes on a full-bleed band, which butts
        // straight into the footer's "Stay Updated" band. Padding here would
        // show as a strip of page ground between two coloured bands.
        <div>
            {page.hero && (
                page.hero.image ? (
                    <section style={{
                        position: 'relative', minHeight: MEDIA.hero.minHeight, display: 'flex',
                        alignItems: 'center', justifyContent: heroLeft ? 'flex-start' : 'center',
                        textAlign: heroLeft ? 'left' : 'center',
                        backgroundImage: `linear-gradient(${MEDIA.hero.overlay}, ${MEDIA.hero.overlay}), url(${page.hero.image})`,
                        // `hero.focus` nudges the crop when the subject isn't centred.
                        backgroundSize: 'cover', backgroundPosition: page.hero.focus || 'center',
                        marginBottom: '32px',
                    }}>
                        <div style={{
                            padding: heroLeft ? `${SPACE.bandTop}px 8vw` : `${SPACE.bandTop}px ${PAD}px`,
                            color: '#fff', width: '100%',
                        }}>
                            {!below && heroSubtitle(false, 'rgba(255,255,255,0.9)')}
                            <h1 style={{
                                ...HEADING, lineHeight: 1.15, margin: 0,
                                fontSize: heroLeft ? TYPE.displayHeading : TYPE.heroTitle,
                            }}>
                                {page.hero.title}
                            </h1>
                            {below && heroSubtitle(true, 'rgba(255,255,255,0.92)')}
                        </div>
                    </section>
                ) : (
                    <section style={{ textAlign: 'center', padding: `${SPACE.bandTop}px ${PAD}px 8px` }}>
                        {!below && heroSubtitle(false, COLORS.muted)}
                        <h1 style={{ ...HEADING, fontSize: TYPE.heroTitle, lineHeight: 1.15, margin: 0, color: COLORS.dark }}>
                            {page.hero.title}
                        </h1>
                        {below && heroSubtitle(true, COLORS.text)}
                    </section>
                )
            )}
            {page.blocks.map((block, i) => <Block key={i} block={block} />)}
        </div>
    );
}
